import { useCallback, useState } from 'react';
import { SSEvent } from 'sse.js';

import { environment } from '@env';

import { useAuth } from '@/context/AuthProvider';
import { useSSE } from '@/hooks/useSse';

export interface StreamCommunicateRequest {
  message: string;
  session_id?: string;
  property_id?: string;
  location_id?: string;
  document_id?: string;
  chat_type?: string;
  message_id?: string;
}

interface StreamResponse {
  content?: string;
  data?: unknown;
  metadata?: Record<string, unknown>;
  email?: string;
  urgent?: boolean;
  intent?: string;
  session_id?: string;
}

export interface StreamMessage {
  type: 'connected' | 'status' | 'document' | 'response' | 'completed' | 'error';
  message: string;
  document_id?: string;
  document_name?: string;
  response?: StreamResponse;
  error?: Error | string;
}

interface UseStreamCommunicateProps {
  onMessage?: (message: StreamMessage) => void;
  onError?: (error: Error) => void;
  onComplete?: () => void;
}

export const useStreamCommunicate = ({
  onMessage,
  onError,
  onComplete,
}: UseStreamCommunicateProps = {}) => {
  const { session } = useAuth();
  const [currentRequest, setCurrentRequest] = useState<StreamCommunicateRequest | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);

  const buildEndpointUrl = useCallback((request: StreamCommunicateRequest): string => {
    const params = new URLSearchParams();

    params.append('property_id', request.property_id || '');
    params.append('location_id', request.location_id || '');
    params.append('message', request.message || '');

    if (request.session_id) params.append('session_id', request.session_id);
    if (request.document_id) params.append('document_id', request.document_id);
    if (request.chat_type) params.append('chat_type', request.chat_type);
    if (request.message_id) params.append('message_id', request.message_id);

    return `${environment.direct}/simulation/communicate/stream?${params.toString()}`;
  }, []);

  const handleMessage = useCallback(
    (event: SSEvent) => {
      try {
        const data = JSON.parse(event.data) as StreamMessage;

        onMessage?.(data);

        if (data.type === 'completed') {
          setIsStreaming(false);
          setCurrentRequest(null);
          onComplete?.();
        } else if (data.type === 'error') {
          setIsStreaming(false);
          setCurrentRequest(null);
          onError?.(data.error as Error);
        }
      } catch (error) {
        setIsStreaming(false);
        setCurrentRequest(null);
        onError?.(error as Error);
      }
    },
    [onMessage, onError, onComplete]
  );

  const handleError = useCallback(() => {
    setIsStreaming(false);
    setCurrentRequest(null);
    onError?.(new Error('Connection error'));
  }, [onError]);

  const endpointUrl = currentRequest ? buildEndpointUrl(currentRequest) : '';

  useSSE({
    endpoint: endpointUrl,
    headers: {
      Authorization: `Bearer ${session?.access_token ?? ''}`,
    },
    onMessage: handleMessage,
    onError: handleError,
    enabled: isStreaming && !!endpointUrl && !!session?.access_token,
  });

  const startStream = useCallback((request: StreamCommunicateRequest) => {
    setCurrentRequest(request);
    setIsStreaming(true);
  }, []);

  const stopStream = useCallback(() => {
    setIsStreaming(false);
    setCurrentRequest(null);
  }, []);

  return {
    startStream,
    stopStream,
    isStreaming,
    currentRequest,
  };
};
